// Tabla de cuentas del pulso (como la del panel de Calypso del CRM de Santi): las que más venden hoy,
// con sus métricas, ordenable por cualquier columna. Sin las cuentas internas (como el CRM), así que
// cuadra con la cifra de arriba. Los datos salen del rincón de cuentas (se refresca cada 5 min).
import { ArrowDown, ArrowUp } from 'lucide-react';
import { useMemo, useState } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { dinero, numero, SIN_VALOR } from './formato';
import { VerTodas } from './piezas';
import type { CuentaSala } from './tipos';

type Columna = 'cuenta' | 'ventas' | 'porMsg' | 'porFan' | 'unlock' | 'golden' | 'fans' | 'cvr' | 'reply';

const de = (a: number, b: number): number | null => (b > 0 ? a / b : null);

/** Valor de cada columna para ordenar (null va siempre al final). */
const VALOR: Record<Exclude<Columna, 'cuenta'>, (x: CuentaSala) => number | null> = {
  ventas: (x) => x.ventas,
  porMsg: (x) => de(x.ventas, x.mensajes),
  porFan: (x) => de(x.ventas, x.fansChatean),
  unlock: (x) => (x.ppvEnviados ? (100 * x.ppvComprados) / x.ppvEnviados : null),
  golden: (x) => (x.mensajes ? (100 * x.ppvEnviados) / x.mensajes : null),
  fans: (x) => x.fansChatean,
  cvr: (x) => (x.fansChatean ? (100 * x.fansPagan) / x.fansChatean : null),
  reply: (x) => x.respuestaS,
};

const FORMATO: Record<Exclude<Columna, 'cuenta'>, (v: number) => string> = {
  ventas: (v) => dinero(v),
  porMsg: (v) => dinero(v, 3),
  porFan: (v) => dinero(v, 2),
  unlock: (v) => `${numero(v, 1)} %`,
  golden: (v) => `${numero(v, 1)} %`,
  fans: (v) => numero(v),
  cvr: (v) => `${numero(v, 1)} %`,
  reply: (v) => `${numero(v)} s`,
};

const NUMERICAS = Object.keys(VALOR) as Array<Exclude<Columna, 'cuenta'>>;

export function nombreCuenta(x: CuentaSala): string {
  return x.nombre ?? x.usuario ?? x.id.slice(0, 8);
}

/** `aLaVista`: cuántas se ven antes de «Ver las N» (se ordenan todas y se enseñan las primeras de ese orden). */
export function TablaCuentas({ cuentas, aLaVista }: { cuentas: CuentaSala[]; aLaVista?: number }) {
  const [orden, setOrden] = useState<{ col: Columna; asc: boolean }>({ col: 'ventas', asc: false });
  const [todas, setTodas] = useState(false);
  const recortable = aLaVista !== undefined && cuentas.length > aLaVista;
  const filas = useMemo(() => {
    const copia = [...cuentas];
    if (orden.col === 'cuenta') {
      copia.sort((a, b) => nombreCuenta(a).localeCompare(nombreCuenta(b), 'es') * (orden.asc ? 1 : -1));
      return copia;
    }
    const valor = VALOR[orden.col];
    copia.sort((a, b) => {
      const va = valor(a);
      const vb = valor(b);
      if (va === null) return vb === null ? 0 : 1;
      if (vb === null) return -1;
      return orden.asc ? va - vb : vb - va;
    });
    return copia;
  }, [cuentas, orden]);

  const cabecera = (col: Columna, derecha: boolean) => {
    const activa = orden.col === col;
    return (
      <th key={col} className={cn('sticky top-0 z-[1] bg-oficina-elevada px-1.5 py-2 font-medium', derecha ? 'text-right' : 'text-left')}>
        <button
          type="button"
          onClick={() => setOrden((o) => ({ col, asc: o.col === col ? !o.asc : col === 'cuenta' }))}
          className={cn('inline-flex items-center gap-1 hover:text-oficina-texto', activa && 'text-oficina-texto')}
        >
          {t(`salaControl.tabla.${col}`)}
          {activa ? (orden.asc ? <ArrowUp className="h-3 w-3" aria-hidden /> : <ArrowDown className="h-3 w-3" aria-hidden />) : null}
        </button>
      </th>
    );
  };

  const visibles = recortable && !todas ? filas.slice(0, aLaVista) : filas;
  // 10-10: márgenes de 6 px y la cuenta hasta 13rem (se corta con «…») para que las 10 columnas quepan en la ventana de un
  // portátil (~830 px) sin que «Estado» se salga por la derecha.
  return (
    <>
    <table className="w-full text-o-sub">
      <thead>
        <tr className="text-o-nota text-oficina-texto3">
          {cabecera('cuenta', false)}
          {NUMERICAS.map((c) => cabecera(c, true))}
          <th className="sticky top-0 z-[1] bg-oficina-elevada px-1.5 py-2 text-left font-medium">{t('salaControl.tabla.estado')}</th>
        </tr>
      </thead>
      <tbody>
        {visibles.map((x, i) => (
          <tr key={x.id} className="border-t border-oficina-linea hover:bg-oficina-hover">
            <td className="max-w-[13rem] truncate py-1.5 pl-2 pr-1.5">
              <span className="mr-2 inline-block w-6 text-right text-o-nota text-oficina-texto3">{i + 1}</span>
              {nombreCuenta(x)}
              {x.usuario && x.nombre ? <span className="ml-1.5 text-o-nota text-oficina-texto3">{`@${x.usuario}`}</span> : null}
            </td>
            {NUMERICAS.map((c) => {
              const v = VALOR[c](x);
              return (
                <td key={c} className={cn('whitespace-nowrap px-1.5 text-right tabular-nums', c === 'ventas' && 'font-semibold')}>
                  {v === null ? SIN_VALOR : FORMATO[c](v)}
                </td>
              );
            })}
            <td className="whitespace-nowrap px-1.5">
              {x.parada || x.callada ? (
                <span className={cn('inline-flex items-center gap-1.5 text-o-nota', x.parada ? 'text-oficina-error' : 'text-oficina-bloqueado')}>
                  <span className={cn('h-2 w-2 rounded-full', x.parada ? 'bg-oficina-error' : 'bg-oficina-bloqueado')} />
                  {t(x.parada ? 'salaControl.tabla.parada' : 'salaControl.tabla.callada')}
                </span>
              ) : (
                <span className={cn('inline-block h-2 w-2 rounded-full', x.respuestasUltimaHora ? 'bg-oficina-trabajando' : 'bg-oficina-esperando')} />
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    {recortable ? <VerTodas total={cuentas.length} abiertas={todas} alCambiar={() => setTodas((x) => !x)} /> : null}
    </>
  );
}
